import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowLeftIcon,
  CheckIcon,
  TruckIcon,
  ArchiveBoxXMarkIcon,
  ArrowPathIcon,
  CurrencyEuroIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid'
import {
  ShoppingCartIcon,
  BuildingOfficeIcon,
  UserIcon,
  DocumentTextIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'
import { ordersApi, sapIntegrationApi, type OrderWithRelations } from '../../lib/api'

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  confirmed: { bg: 'bg-blue-100', text: 'text-blue-800' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  shipped: { bg: 'bg-purple-100', text: 'text-purple-800' },
  delivered: { bg: 'bg-green-100', text: 'text-green-800' },
  invoiced: { bg: 'bg-teal-100', text: 'text-teal-800' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
}

const STATUS_FLOW = ['draft', 'confirmed', 'processing', 'shipped', 'delivered', 'invoiced']

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['orders', id],
    queryFn: () => ordersApi.get(id!),
    enabled: !!id,
  })

  const confirmMutation = useMutation({
    mutationFn: () => ordersApi.confirm(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const processMutation = useMutation({
    mutationFn: () => ordersApi.process(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const shipMutation = useMutation({
    mutationFn: () => ordersApi.ship(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const deliverMutation = useMutation({
    mutationFn: () => ordersApi.deliver(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const invoiceMutation = useMutation({
    mutationFn: () => ordersApi.invoice(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => ordersApi.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'stats'] })
    },
  })

  const sapPushMutation = useMutation({
    mutationFn: () => sapIntegrationApi.pushOrder(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
    },
  })

  const sapRetryMutation = useMutation({
    mutationFn: () => sapIntegrationApi.retryOrderPush(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] })
    },
  })

  if (isLoading) return <div className="p-8">Loading...</div>
  if (error || !order) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">Order not found</h2>
          <Link to="/orders" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
            Back to Orders
          </Link>
        </div>
      </div>
    )
  }

  const formatPrice = (price: string | null, currency: string = 'EUR') => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  const formatDate = (date: string | Date | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const currentStatusIndex = STATUS_FLOW.indexOf(order.status)
  const canAdvance = order.status !== 'cancelled' && order.status !== 'invoiced'

  const handleStatusAdvance = () => {
    const nextStatus = STATUS_FLOW[currentStatusIndex + 1]
    if (!nextStatus) return

    const confirmed = confirm(`Move order to "${nextStatus}" status?`)
    if (!confirmed) return

    switch (nextStatus) {
      case 'confirmed':
        confirmMutation.mutate()
        break
      case 'processing':
        processMutation.mutate()
        break
      case 'shipped':
        shipMutation.mutate()
        break
      case 'delivered':
        deliverMutation.mutate()
        break
      case 'invoiced':
        invoiceMutation.mutate()
        break
    }
  }

  const handleCancel = () => {
    if (confirm('Cancel this order? This action cannot be undone.')) {
      cancelMutation.mutate()
    }
  }

  return (
    <>
      {/* Secondary column - Order info */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <Link
              to="/orders"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Orders
            </Link>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-brand-100 flex items-center justify-center">
                <ShoppingCartIcon className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{order.orderNumber}</h2>
                <p className="text-sm text-gray-500">
                  {order.account?.name || order.cardName || 'No account'}
                </p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full ${STATUS_COLORS[order.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[order.status]?.text || 'text-gray-700'}`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>

          {/* Status Progress */}
          {order.status !== 'cancelled' && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Order Progress</h3>
              <div className="flex items-center justify-between">
                {STATUS_FLOW.map((status, index) => {
                  const isCompleted = index <= currentStatusIndex
                  const isCurrent = index === currentStatusIndex
                  return (
                    <div key={status} className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        } ${isCurrent ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}
                      >
                        {isCompleted ? <CheckIcon className="h-4 w-4" /> : index + 1}
                      </div>
                      <span className="text-xs text-gray-500 mt-1 capitalize">{status}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            {canAdvance && currentStatusIndex < STATUS_FLOW.length - 1 && (
              <button
                onClick={handleStatusAdvance}
                disabled={confirmMutation.isPending || processMutation.isPending || shipMutation.isPending || deliverMutation.isPending || invoiceMutation.isPending}
                className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
              >
                {order.status === 'draft' && <CheckIcon className="h-4 w-4" />}
                {order.status === 'confirmed' && <ArrowPathIcon className="h-4 w-4" />}
                {order.status === 'processing' && <TruckIcon className="h-4 w-4" />}
                {order.status === 'shipped' && <CheckIcon className="h-4 w-4" />}
                {order.status === 'delivered' && <CurrencyEuroIcon className="h-4 w-4" />}
                Move to {STATUS_FLOW[currentStatusIndex + 1]?.charAt(0).toUpperCase() + STATUS_FLOW[currentStatusIndex + 1]?.slice(1)}
              </button>
            )}
            {order.status !== 'cancelled' && order.status !== 'invoiced' && (
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-red-600 shadow-sm border border-red-300 hover:bg-red-50 disabled:opacity-50"
              >
                <ArchiveBoxXMarkIcon className="h-4 w-4" />
                Cancel Order
              </button>
            )}
          </div>

          {/* Quick Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Order Details</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">
                  {formatPrice(order.docTotal, order.docCurrency)}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Items</dt>
                <dd className="font-medium text-gray-900">{order._count?.lineItems || order.lineItems?.length || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Currency</dt>
                <dd className="font-medium text-gray-900">{order.docCurrency}</dd>
              </div>
              {order.customerRefNumber && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Customer Ref</dt>
                  <dd className="font-medium text-gray-900">{order.customerRefNumber}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Dates */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Dates</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Order Date</dt>
                <dd className="font-medium text-gray-900">{formatDate(order.docDate)}</dd>
              </div>
              {order.docDueDate && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Due Date</dt>
                  <dd className="font-medium text-gray-900">{formatDate(order.docDueDate)}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Source Quote Link */}
          {order.quoteId && order.quote && (
            <div className="bg-blue-50 p-4 rounded-lg ring-1 ring-blue-200">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Created from Quote</h3>
              <Link
                to={`/quotes/${order.quoteId}`}
                className="text-sm text-blue-700 hover:text-blue-900 underline"
              >
                View Quote {order.quote.quoteNumber}
              </Link>
            </div>
          )}

          {/* SAP Sync Status */}
          <div className={`p-4 rounded-lg ring-1 ${
            order.syncStatus === 'synced'
              ? 'bg-green-50 ring-green-200'
              : order.syncStatus === 'push_failed'
                ? 'bg-red-50 ring-red-200'
                : 'bg-gray-50 ring-gray-200'
          }`}>
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
              <CloudArrowUpIcon className="h-4 w-4" />
              SAP Integration
            </h3>

            {order.externalId ? (
              // Already synced
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircleIcon className="h-4 w-4" />
                  Synced to SAP
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>DocEntry: {order.externalId}</p>
                  {order.externalDocNum && <p>DocNum: {order.externalDocNum}</p>}
                  {order.lastSyncedAt && (
                    <p>Synced: {new Date(order.lastSyncedAt).toLocaleString()}</p>
                  )}
                </div>
              </div>
            ) : order.syncStatus === 'push_failed' ? (
              // Failed
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <XCircleIcon className="h-4 w-4" />
                  Sync Failed
                </div>
                {order.syncError && (
                  <p className="text-xs text-red-600">{order.syncError}</p>
                )}
                <button
                  onClick={() => sapRetryMutation.mutate()}
                  disabled={sapRetryMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${sapRetryMutation.isPending ? 'animate-spin' : ''}`} />
                  Retry Push
                </button>
              </div>
            ) : order.syncStatus === 'push_pending' ? (
              // Pending
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-yellow-700">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  Push Pending
                </div>
                <p className="text-xs text-gray-600">Will be pushed on next sync cycle</p>
              </div>
            ) : (
              // Not synced
              <div className="space-y-2">
                <p className="text-xs text-gray-600">Not synced to SAP yet</p>
                {order.status === 'confirmed' && (
                  <button
                    onClick={() => sapPushMutation.mutate()}
                    disabled={sapPushMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
                  >
                    <CloudArrowUpIcon className={`h-4 w-4 ${sapPushMutation.isPending ? 'animate-spin' : ''}`} />
                    Push to SAP
                  </button>
                )}
                {order.status !== 'confirmed' && (
                  <p className="text-xs text-gray-500 italic">
                    Orders must be confirmed before pushing to SAP
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile back link */}
          <div className="xl:hidden mb-6">
            <Link
              to="/orders"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Orders
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Order {order.orderNumber}</h1>
            <p className="mt-1 text-gray-500">Created on {formatDate(order.docDate)}</p>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Account */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Account</h3>
              </div>
              {order.account ? (
                <Link
                  to={`/accounts/${order.accountId}`}
                  className="text-brand-600 hover:text-brand-700 font-medium"
                >
                  {order.account.name}
                </Link>
              ) : (
                <p className="text-gray-500">{order.cardName || 'No account selected'}</p>
              )}
              {order.cardCode && (
                <p className="text-sm text-gray-500 mt-1">Customer #: {order.cardCode}</p>
              )}
            </div>

            {/* Contact */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Contact</h3>
              </div>
              {order.contact ? (
                <Link
                  to={`/contacts/${order.contactId}`}
                  className="text-brand-600 hover:text-brand-700 font-medium"
                >
                  {order.contact.firstName} {order.contact.lastName}
                </Link>
              ) : (
                <p className="text-gray-500">No contact selected</p>
              )}
            </div>
          </div>

          {/* Addresses */}
          {(order.shippingAddress || order.billingAddress) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {order.shippingAddress && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPinIcon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900">Shipping Address</h3>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{order.shippingAddress}</p>
                </div>
              )}
              {order.billingAddress && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900">Billing Address</h3>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{order.billingAddress}</p>
                </div>
              )}
            </div>
          )}

          {/* Line Items */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h2>
            {order.lineItems && order.lineItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {order.lineItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {item.lineNum + 1}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.itemCode || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {item.itemDescription}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {item.quantity} {item.uomCode || ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatPrice(item.unitPrice, item.currency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                          {item.discountPercent}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatPrice(item.lineTotal, item.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={6} className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        Subtotal
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatPrice(
                          order.docTotalBeforeDiscount || order.lineItems.reduce((sum, item) => sum + parseFloat(item.lineTotal || '0'), 0).toString(),
                          order.docCurrency
                        )}
                      </td>
                    </tr>
                    {order.vatSum && parseFloat(order.vatSum) > 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-right text-sm text-gray-500">
                          VAT
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {formatPrice(order.vatSum, order.docCurrency)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={6} className="px-4 py-3 text-right text-base font-bold text-gray-900">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right text-base font-bold text-gray-900">
                        {formatPrice(order.docTotal, order.docCurrency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-500">No line items</p>
              </div>
            )}
          </div>

          {/* Comments */}
          {order.comments && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Comments</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{order.comments}</p>
              </div>
            </div>
          )}

          {/* Payment & Shipping Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {(order.paymentTermsName || order.paymentTermsCode) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Payment Terms</h3>
                <p className="text-sm text-gray-600">{order.paymentTermsName || `Code: ${order.paymentTermsCode}`}</p>
              </div>
            )}
            {(order.shippingTypeName || order.shippingTypeCode) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Shipping Method</h3>
                <p className="text-sm text-gray-600">{order.shippingTypeName || `Code: ${order.shippingTypeCode}`}</p>
              </div>
            )}
          </div>

          {/* Opportunity Link */}
          {order.opportunity && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Linked Opportunity</h3>
              <Link
                to={`/opportunities/${order.opportunityId}`}
                className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700"
              >
                {order.opportunity.name}
              </Link>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t pt-6 mt-8">
            <dl className="flex flex-wrap gap-8 text-xs text-gray-500">
              <div>
                <dt className="inline">Created: </dt>
                <dd className="inline">{formatDate(order.createdAt)}</dd>
              </div>
              <div>
                <dt className="inline">Updated: </dt>
                <dd className="inline">{formatDate(order.updatedAt)}</dd>
              </div>
              {order.owner && (
                <div>
                  <dt className="inline">Owner: </dt>
                  <dd className="inline">{order.owner.firstName} {order.owner.lastName}</dd>
                </div>
              )}
              {order.source !== 'manual' && (
                <div>
                  <dt className="inline">Source: </dt>
                  <dd className="inline capitalize">{order.source.replace('_', ' ')}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </>
  )
}
